import React, { useRef, useCallback, useEffect } from "react";
import { Modal, Box, Button } from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import PrintIcon from "@mui/icons-material/Print";
import logo from "./logo.png";
import { getUnidadeData } from "../../utils/YEARCONSTS";
import { calculateMediaFinalAfterExam } from "./utils";

const PrintableReport = ({
  open,
  onClose,
  aluno,
  subjects,
  formattedGrades,
  finalExams,
  studentStatus,
  unidade = "CAMPINA GRANDE",
  exportarAutomaticamente = false
}) => {
  const reportRef = useRef(null);
  const unidadeData = getUnidadeData(unidade);

  const getGradeForSubject = useCallback((disciplinaId, bimester, grades) => {
    const period = `period${bimester}`;
    const grade = grades[period]?.find((g) => String(g.DisciplinaID) === String(disciplinaId));
    
    if (!grade || !grade.mb) return "-";
    
    // Se a nota usou recuperação, adiciona um marcador
    if (grade.used_recovery) {
      return `${grade.mb}*`;
    }
    
    return grade.mb;
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateFinalAverage = useCallback(
    (disciplinaId, grades) => {
      const bimesters = [1, 2, 3, 4];
      const validGrades = [];
      const usedRecoveryInAnyPeriod = [];
      
      // Coletar todas as notas válidas e verificar recuperação
      for (const bim of bimesters) {
        const period = `period${bim}`;
        const grade = grades[period]?.find((g) => String(g.DisciplinaID) === String(disciplinaId));
        
        if (grade && grade.mb) {
          validGrades.push(parseFloat(grade.mb.replace(",", ".")));
          if (grade.used_recovery) {
            usedRecoveryInAnyPeriod.push(true);
          }
        }
      }
      
      // Se não tiver notas, retornar "-"
      if (validGrades.length === 0) return "-";
      
      // Calcular a média
      const sum = validGrades.reduce((acc, grade) => acc + grade, 0);
      const average = (sum / validGrades.length).toFixed(1).replace(".", ",");
      
      // Adicionar indicador se alguma nota veio de recuperação
      return usedRecoveryInAnyPeriod.length > 0 ? `${average}*` : average;
    },
    []
  );

  const handleGeneratePDF = useCallback(async () => {
    if (!reportRef.current) return;

    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const pdf = new jsPDF({
        format: "a4",
        unit: "mm",
      });

      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      
      // Se for exportação automática, retornar o blob do PDF em vez de abrir em nova janela
      if (exportarAutomaticamente) {
        return pdf;
      } else {
        const pdfBlob = pdf.output("bloburl");
        window.open(pdfBlob, "_blank");
      }
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      return null;
    }
  }, [exportarAutomaticamente]);
  
  // Efeito para exportar PDF automaticamente quando necessário
  useEffect(() => {
    if (exportarAutomaticamente && open && reportRef.current) {
      // Pequeno delay para garantir que o DOM esteja renderizado
      const timeoutId = setTimeout(async () => {
        try {
          const canvas = await html2canvas(reportRef.current, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: "#ffffff",
          });

          const pdf = new jsPDF({
            format: "a4",
            unit: "mm",
            orientation: "portrait",
          });

          const imgWidth = 210; // A4 width em mm
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          const imgData = canvas.toDataURL("image/png");
          
          pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
          
          // Nome do arquivo baseado no nome do aluno
          const nomeAluno = aluno.nome.split("-")[1]?.trim() || "boletim";
          // eslint-disable-next-line no-unused-vars
          const nomeArquivo = `Boletim_${nomeAluno.replace(/\s+/g, '_')}.pdf`;
          
          // Abrir o PDF em uma nova aba
          const pdfBlob = pdf.output("bloburl");
          window.open(pdfBlob, "_blank");
          
          if (onClose) onClose();
        } catch (error) {
          console.error("Erro ao gerar PDF automático:", error);
          if (onClose) onClose();
        }
      }, 800); // Um pouco mais de tempo para garantir que tudo esteja renderizado
      
      return () => clearTimeout(timeoutId);
    }
  }, [exportarAutomaticamente, open, onClose, aluno.nome]);

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="printable-report">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          width: "160mm",
          maxWidth: "95vw",
          bgcolor: "background.paper",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
          borderRadius: "12px",
          p: 4,
          overflowY: "auto",
          "&:focus": {
            outline: "none",
          },
        }}
      >
        <div ref={reportRef} style={{ background: "#fff", padding: "20px" }}>
          {/* Cabeçalho com informações da escola */}
          <div style={{ textAlign: "center", marginBottom: "20px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "12px",
                gap: "12px",
              }}
            >
              <img src={logo} alt="Logo Autêntico" style={{ height: "80px" }} />
              <div>
                <h2
                  style={{
                    fontSize: "18px",
                    margin: "0",
                    color: "#1a237e",
                    fontWeight: "600",
                  }}
                >
                  {unidadeData.nome}
                </h2>
                <p
                  style={{
                    fontSize: "12px",
                    margin: "4px 0",
                    color: "#666",
                  }}
                >
                  {unidadeData.endereco}
                </p>
                <p
                  style={{
                    fontSize: "12px",
                    margin: "2px 0",
                    color: "#666",
                  }}
                >
                  {unidadeData.website} • {unidadeData.telefone}
                </p>
              </div>
            </div>

            <div
              style={{
                backgroundColor: "#1a237e",
                padding: "8px",
                borderRadius: "6px",
                color: "white",
                marginBottom: "10px",
              }}
            >
              <h1
                style={{
                  fontSize: "16px",
                  margin: 0,
                  fontWeight: "600",
                  letterSpacing: "0.5px",
                }}
              >
                BOLETIM ESCOLAR
              </h1>
            </div>
          </div>

          {/* Informações do aluno */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "20px",
              marginBottom: "10px",
              fontSize: "15px",
              padding: "10px",
              backgroundColor: "#f8f9fa",
              borderRadius: "6px",
            }}
          >
            <div>
              <strong style={{ color: "#1a237e" }}>Aluno:</strong>{" "}
              {aluno.nome.split("-")[1]}
            </div>
            <div>
              <strong style={{ color: "#1a237e" }}>Ano Letivo: </strong>
              {unidadeData.anoLetivo}
            </div>
          </div>

          {/* Tabela de notas */}
          <div
            style={{
              border: "1px solid #e0e0e0",
              borderRadius: "6px",
              overflow: "hidden",
            }}
          >
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "14px",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f8f9fa" }}>
                  <th
                    style={{
                      padding: "10px",
                      textAlign: "left",
                      color: "#1a237e",
                      borderBottom: "2px solid #e0e0e0",
                      width: "25%",
                    }}
                  >
                    Disciplinas
                  </th>
                  {[1, 2, 3, 4].map((bim) => (
                    <th
                      key={bim}
                      style={{
                        padding: "16px",
                        textAlign: "center",
                        color: "#1a237e",
                        borderBottom: "2px solid #e0e0e0",
                      }}
                    >
                      {bim}º Bimestre
                    </th>
                  ))}
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      color: "#1a237e",
                      borderBottom: "2px solid #e0e0e0",
                    }}
                  >
                    MA
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      color: "#1a237e",
                      borderBottom: "2px solid #e0e0e0",
                    }}
                  >
                    EF
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      color: "#1a237e",
                      borderBottom: "2px solid #e0e0e0",
                    }}
                  >
                    MF
                  </th>
                  <th
                    style={{
                      padding: "16px",
                      textAlign: "center",
                      color: "#1a237e",
                      borderBottom: "2px solid #e0e0e0",
                    }}
                  >
                    Situação
                  </th>
                </tr>
              </thead>
              <tbody>
                {subjects.map((subject, index) => {
                  const mediaAnual = calculateFinalAverage(
                    subject.DisciplinaID,
                    formattedGrades
                  );
                  const mediaFinal = calculateMediaFinalAfterExam(
                    mediaAnual,
                    finalExams[subject.DisciplinaID]
                  );
                  return (
                  <tr
                    key={subject.DisciplinaID}
                    style={{
                      backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                    }}
                  >
                    <td
                      style={{
                        padding: "8px 10px",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      {subject.Nome}
                    </td>
                    {[1, 2, 3, 4].map((bim) => (
                      <td
                        key={bim}
                        style={{
                          padding: "8px",
                          textAlign: "center",
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        {getGradeForSubject(
                          subject.DisciplinaID,
                          bim,
                          formattedGrades
                        )}
                      </td>
                    ))}
                    <td
                      style={{
                        padding: "14px",
                        textAlign: "center",
                        borderBottom: "1px solid #e0e0e0",
                        fontWeight: "500",
                      }}
                    >
                      {mediaAnual}
                    </td>
                    <td
                      style={{
                        padding: "14px",
                        textAlign: "center",
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      {finalExams[subject.DisciplinaID] || "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px",
                        textAlign: "center",
                        borderBottom: "1px solid #e0e0e0",
                        fontWeight: "500",
                      }}
                    >
                      {mediaFinal}
                    </td>
                    <td
                      style={{
                        padding: "14px",
                        textAlign: "center",
                        borderBottom: "1px solid #e0e0e0",
                        color:
                          studentStatus[subject.DisciplinaID] === "APROVADO"
                            ? "#2e7d32"
                            : studentStatus[subject.DisciplinaID] === "EM EXAME"
                            ? "#ed6c02"
                            : "#666",
                      }}
                    >
                      {studentStatus[subject.DisciplinaID] || "CURSANDO"}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Legenda */}
          <div
            style={{
              fontSize: "13px",
              marginTop: "24px",
              color: "#666",
              display: "flex",
              gap: "20px",
              flexWrap: "wrap"
            }}
          >
            <span>
              <strong>MA</strong> - Média Anual
            </span>
            <span>
              <strong>MF</strong> - Média Final (após exame; sem EF, igual à MA)
            </span>
            <span>
              <strong>EF</strong> - Exame Final
            </span>
            <span>
              <strong>*</strong> - Nota após recuperação
            </span>
          </div>
        </div>

        {!exportarAutomaticamente && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              startIcon={<PrintIcon />}
              onClick={handleGeneratePDF}
            >
              Imprimir / Exportar PDF
            </Button>
          </div>
        )}
      </Box>
    </Modal>
  );
};

export default PrintableReport;
